/**
 * Array utility functions
 */

/**
 * Represents an object with string keys
 */
type ObjectWithStringKeys = {
  [key: string]: unknown;
};

/**
 * Groups array items by a specified property
 */
export const groupBy = <T extends ObjectWithStringKeys, K extends keyof T>(
  items: T[],
  group: K
): Record<string, T[]> => {
  return items.reduce((acc: Record<string, T[]>, item: T) => {
    const key = String(item[group]);
    acc[key] = [...(acc[key] || []), item];
    return acc;
  }, {});
};

/**
 * Gets the minimum value of a specified field from an array of objects
 */
export const getMinItemByField = <T extends ObjectWithStringKeys>(
  data: T[],
  field: keyof T
): T | null => {
  if (!data.length) return null;
  return data.reduce((min, p) => (p[field] < min[field] ? p : min), data[0]);
};

/**
 * Merges two arrays without duplicates based on a specified property
 */
export const mergeWithoutDuplicates = <T extends ObjectWithStringKeys>(
  arr1: T[],
  arr2: T[],
  property: keyof T
): T[] => {
  const merged = [...arr1];
  arr2.forEach((item) => {
    if (!merged.some((existingItem) => existingItem[property] === item[property])) {
      merged.push(item);
    }
  });
  return merged;
};

/**
 * Array utility interface
 */
interface ArrayUtils {
  groupBy: <T extends ObjectWithStringKeys, K extends keyof T>(
    items: T[],
    group: K
  ) => Record<string, T[]>;
  getMinItemByField: <T extends ObjectWithStringKeys>(
    data: T[],
    field: keyof T
  ) => T | null;
  mergeWithoutDuplicates: <T extends ObjectWithStringKeys>(
    arr1: T[],
    arr2: T[],
    property: keyof T
  ) => T[];
}

const array: ArrayUtils = {
  groupBy,
  getMinItemByField,
  mergeWithoutDuplicates,
};

export default array;
